import { Component, Renderer2, effect, inject, OnInit, Signal, AfterViewInit, OnDestroy, signal } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Route, Router, RouterOutlet } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { ThemeService } from "../app/other/theme/theme.service";
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { UserinfoFaqComponent } from "./admin/components/userinfo-faq/userinfo-faq.component";
import { WebsiteStateService } from "./state-management/services/website-state.service";
import { PageComponent } from "./pages/page/page.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ToggleEditModeComponent } from "./components/meta/toggle-edit-mode/toggle-edit-mode.component";
import { TestingComponent } from "./testing/testing/testing.component";
import { WebsiteConfigComponent } from "./components/meta/website-config/website-config.component";
import { Page, Theming } from "./state-management/model/interfaces/state.model";

import { isPlatformBrowser } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';
import { Auth, onAuthStateChanged, User } from "@angular/fire/auth";
import { UserStateService } from "./state-management/services/user-state.service";
import { LogoutComponent } from "./admin/components/logout/logout.component";
import { AuthGuard } from "./admin/auth.guard";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { environment } from "../environments/environment";
import { SaveComponent } from "./admin/components/save/save.component";
import { ImpressumComponent } from "./pages/impressum/impressum.component";
import { DataPrivacyComponent } from "./pages/data-privacy/data-privacy.component";

import { ConsentManagementService } from "./services/consent-management.service";
import { AnalyticsService } from "./services/analytics.service";
import { DiscountCodeManagerService } from "./services/discount-code-manager.service";
import { filter, map, mergeMap, Subscription } from "rxjs";
import { PaymentInfoComponent } from "./admin/components/payment-info/payment-info.component";
import { SpecialOpeningHoursDialogService } from "./services/special-opening-hours-dialog.service";
import { NotificationSpeicalOpeningHoursComponent } from "./components/meta/notification-speical-opening-hours/notification-speical-opening-hours.component";
import { UserinfoComponent } from "./admin/components/userinfo/userinfo.component";

import { Title } from '@angular/platform-browser';
import { RedirectComponent } from "./pages/redirect/redirect.component";
import { LoggerService } from "./services/logger.service";
import { QuickConnectComponent } from "./components/static/quick-connect/quick-connect.component";
import { DiscountBannerComponent } from "./components/meta/notification-discount/discount-banner/discount-banner.component";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    UserinfoComponent,
    UserinfoFaqComponent,
    PaymentInfoComponent,
    NotificationSpeicalOpeningHoursComponent,
    LogoutComponent,
    SaveComponent,
    DragDropModule,
    ToggleEditModeComponent,
    WebsiteConfigComponent,
    QuickConnectComponent,
    DiscountBannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'flower_stores';
  #themeService = inject(ThemeService);
  #renderer = inject(Renderer2);

  #pages: Signal<Record<string, Page>> = this.stateService.pages;
  #pageKeys: Signal<string[]> = this.stateService.pageKeys;
  #theming: Signal<Theming> = this.stateService.theming;

  public loggedIn: Signal<boolean> = this.userStateService.loggedIn;
  public isAdminPage = environment.isAdminPage
  public showAdminPanel = false;
  public isLoaded = false;

  private showCookieBanner: boolean = true;
  private cookieConsentInitialized: boolean = false;

  private breakpointSubscription: Subscription = new Subscription();

  // === DISCOUNT BANNER STATE ===
  public discountBannerVisible = signal(false);
  public discountCode = '';
  public discountText = '';


  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private route: ActivatedRoute,
    private auth: Auth,
    private userStateService: UserStateService,
    private stateService: WebsiteStateService,
    private logger: LoggerService,
    private consentManagementService: ConsentManagementService,
    private analyticsService: AnalyticsService,
    private discountCodeManager: DiscountCodeManagerService,
    public specialOpeningHoursDialogService: SpecialOpeningHoursDialogService,
    private titleService: Title,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {

    this.titleService.setTitle(this.stateService.websiteData().name);

    effect(() => {
      this.logger.log('Update theming');
      this.#updateTheming();
    });

    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.#triggerUpdateRoutes();
      } else {
        this.logger.warn('Skipping effects: not running in the browser.');
      }
    });

    // === DISCOUNT BANNER SUBSCRIPTION ===
    if (isPlatformBrowser(this.platformId) && !environment.isAdminPage) {
      this.discountCodeManager.showBanner$.subscribe(banner => {
        this.discountBannerVisible.update((_) => banner.isVisible);
        this.discountCode = banner.code;
        this.discountText = banner.discountText;
      });
    }

  }

  ngOnInit() {
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.userStateService.setUser({
          displayName: user.displayName,
          email: user.email,
          emailVerified: user.emailVerified,
          isAnonymous: user.isAnonymous,
          metadata: user.metadata,
          phoneNumber: user.phoneNumber,
          photoURL: user.photoURL
        });
      } else {
        this.userStateService.setUser({
          displayName: null,
          email: null,
          emailVerified: false,
          isAnonymous: false,
          metadata: null,
          phoneNumber: null,
          photoURL: null
        });
      }
    });

    this.#triggerUpdateRoutes();
    // this.#updateTheming();

    this.breakpointSubscription = this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
      Breakpoints.WebPortrait,
      Breakpoints.WebLandscape
    ]).subscribe((state: BreakpointState) => {
      if (state.matches) {
        this.setBodyClass(state);
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        this.showAdminPanel = data.hasOwnProperty('showAdminPanel') ? data['showAdminPanel'] : true;
        this.showCookieBanner = data.hasOwnProperty('showCookieBanner') ? data['showCookieBanner'] : true;

        if (isPlatformBrowser(this.platformId) && !this.isAdminPage) {
          if (this.showCookieBanner && !this.cookieConsentInitialized) {
            this.consentManagementService.initCookieConsent();
            this.cookieConsentInitialized = true;
          }
          // If you need to do something when showCookieBanner is false (e.g., hide an already visible banner),
          // you could add an else if block here.
        }


        const url = this.router.url.split('?')[0]; // URL ohne Query-Parameter
        this.stateService.setActivePageByUrl(url); // Setzt den aktiven Zustand im StateService

        // 📊 Analytics: Page View tracking
        this.analyticsService.trackPageView(url);

        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0); // Scrollt zum Anfang
        }
      });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.specialOpeningHoursDialogService.notificationDialog();
      if (!environment.isAdminPage) {
        this.initializeDiscountSystem()
      }
    }
  }

  ngOnDestroy() {
    this.breakpointSubscription.unsubscribe();
  }

  // === DISCOUNT SYSTEM INTEGRATION ===

  /**
   * Initialisiert das Rabattcode-System für Google Ads-Nutzer
   */
  private initializeDiscountSystem(): void {
    try {
      this.logger.log('🎯 Initializing Discount System');
      this.discountCodeManager.initializeDiscountSystem().then((discountData) => {
        this.logger.log('🎯 Discount System initialized:', discountData);
      })
    } catch (error) {
      this.logger.error('Error initializing discount system:', error);
    }
  }

  // === DISCOUNT BANNER EVENT HANDLERS ===

  onDiscountCodeCopied(code: string): void {
    this.logger.log('🎯 Discount code copied:', code);
  }

  onDiscountBannerClosed(): void {
    this.logger.log('🎯 Discount banner dismissed');
    this.discountCodeManager.dismissBanner();
  }


  setBodyClass(state: BreakpointState) {
    this.#renderer.removeClass(document.body, 'handset-portrait');
    this.#renderer.removeClass(document.body, 'handset-landscape');
    this.#renderer.removeClass(document.body, 'tablet-portrait');
    this.#renderer.removeClass(document.body, 'tablet-landscape');
    this.#renderer.removeClass(document.body, 'web-portrait');
    this.#renderer.removeClass(document.body, 'web-landscape');

    if (state.breakpoints[Breakpoints.HandsetPortrait]) {
      this.#renderer.addClass(document.body, 'handset-portrait');
    } else if (state.breakpoints[Breakpoints.HandsetLandscape]) {
      this.#renderer.addClass(document.body, 'handset-landscape');
    } else if (state.breakpoints[Breakpoints.TabletPortrait]) {
      this.#renderer.addClass(document.body, 'tablet-portrait');
    } else if (state.breakpoints[Breakpoints.TabletLandscape]) {
      this.#renderer.addClass(document.body, 'tablet-landscape');
    } else if (state.breakpoints[Breakpoints.WebPortrait]) {
      this.#renderer.addClass(document.body, 'web-portrait');
    } else if (state.breakpoints[Breakpoints.WebLandscape]) {
      this.#renderer.addClass(document.body, 'web-landscape');
    }
  }


  #triggerUpdateRoutes() {

    if (this.#pages() && this.#pageKeys().length > 0) {
      // ACTUAL WEBSITE PAGES
      const dynamicRoutes: Route[] = Object.values(this.#pages()).map(page => ({
        path: page.url,
        component: PageComponent,
        canActivate: [AuthGuard],
        data: { title: 'Example Page', showAdminPanel: true },
      }));

      // ONLY ADMIN PAGES
      if (this.isAdminPage) {
        dynamicRoutes.push({ path: '', loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule) });
        // dynamicRoutes.push({ path: 'utils/ui', component: TestingComponent, canActivate: [AuthGuard] });
      }
      
      // FIX PAGES
      dynamicRoutes.push({ path: 'impressum', component: ImpressumComponent, canActivate: [AuthGuard], data: { title: 'Impressum', showAdminPanel: false }, });
      dynamicRoutes.push({ path: 'datenschutz', component: DataPrivacyComponent, canActivate: [AuthGuard], data: { title: 'Datenschutzerklärung', showAdminPanel: false }, });
      dynamicRoutes.push({ path: 'qrcode', component: RedirectComponent, canActivate: [AuthGuard], data: { title: 'QR-Code', showAdminPanel: false, showCookieBanner: false }, });
      dynamicRoutes.push({ path: '**', component: NotFoundComponent, canActivate: [AuthGuard], data: { title: 'Example Page', showAdminPanel: false }, });

      // Setze die dynamischen Routen
      this.router.resetConfig(dynamicRoutes);
      this.isLoaded = true;
    } else {
      this.logger.error('Keine Seiten gefunden!');
      const dynamicRoutes: Route[] = [];
      dynamicRoutes.push({ path: 'qrcode', component: RedirectComponent, canActivate: [AuthGuard], data: { title: 'QR-Code', showAdminPanel: false, showCookieBanner: false }, });
      this.router.resetConfig(dynamicRoutes);
      this.isLoaded = true;
    }
  }

  #updateTheming() {
    this.#themeService.updateTheming(this.#theming());
  }
}